import ExportsTab from '@components/timeline/CommandPanel/ExportsTab/ExportsTab';
import Title from '@components/timeline/CommandPanel/ExportsTab/Title';
import { ExpandMore } from '@mui/icons-material';
import { Box, MenuItem, Select, Typography } from '@mui/material';
import { IStoreState } from '@store/index';
import { PermissionType } from '@store/session/types';
import Utils from '@utils/index';
import { first, isEmpty } from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';

const EventInboxExportsPanel: React.FC<{}> = () => {
  const selectedEvents = useSelector((store: IStoreState) => store.overviewView.selectedEvents);
  const sites = useSelector((store: IStoreState) => store.sites.sites);
  const streams = useSelector((store: IStoreState) => store.streams.streams);
  const sessionPermissions = useSelector((store: IStoreState) => store.session.permissions);

  const [selectedCamera, setSelectedCamera] = React.useState<VideoStream | undefined>(undefined);

  const selectedSite = React.useMemo(() => {
    if (!isEmpty(selectedEvents)) {
      const f = first(selectedEvents);
      if (f) {
        const companySites = sites.get(f.companyId);
        if (companySites) {
          return companySites.find(el => el.siteId === f.siteId);
        }
      }
    }
  }, [selectedEvents, sites]);

  const cameraOptions = React.useMemo(() => {
    const kvsNamesSet = new Set<string>([]);
    selectedEvents.forEach((event) => {
      kvsNamesSet.add(event.sourceCamera)
      event.children?.forEach((child) => {
        kvsNamesSet.add(child.sourceCamera)
      })
    })

    const kvsNames = Array.from(kvsNamesSet);

    return streams.filter(el => kvsNames.includes(el.kvsName))
  }, [selectedEvents, streams]);

  React.useEffect(() => {
    if (cameraOptions.length === 1) setSelectedCamera(cameraOptions[0])
    else (setSelectedCamera(first(cameraOptions)))
  }, [cameraOptions])

  const cameraFeatures = React.useMemo(() => {
    if (selectedCamera) {
      const permissions = sessionPermissions[selectedCamera.kvsName];
      const { features } = selectedCamera;
      const { zones, exports, timelapse, imageAnnotations, ptz, ppeDetection, socialDistancing, fireDetection, licensePlateDetection } = features;

      return {
        stream: { view: havePermission(permissions, ["VIEW"]) },

        events: {
          motion: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          zones: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          safetyInfraction: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          person: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          vehicle: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          // legacy
          socialDistance: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          fire: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) },
          vehicleLicensePlate: { active: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]), read: havePermission(permissions, ["EVENTS", "EVENTS_VIEW"]) }
        },

        zones: { active: zones, read: havePermission(permissions, ["GEOFENCES", "GEOFENCES_VIEW"]), write: havePermission(permissions, ["GEOFENCES"]) },
        exports: { active: exports, read: havePermission(permissions, ["VIDEO_DOWNLOADS_VIEW"]), write: havePermission(permissions, ["VIDEO_DOWNLOADS"]) },
        timelapse: { active: timelapse, read: havePermission(permissions, ["TIMELAPSES_VIEW"]), write: havePermission(permissions, ["TIMELAPSES"]) },
        imageAnnotations: { active: imageAnnotations, read: havePermission(permissions, ["ANNOTATED_IMAGES_VIEW"]), write: havePermission(permissions, ["ANNOTATED_IMAGES"]) },

        ptz: { active: ptz, read: havePermission(permissions, ["PTZ_CONTROL"]), write: havePermission(permissions, ["PTZ_CONTROL"]) },

        ppeDetection: ppeDetection,

        socialDistancing: socialDistancing,
        fireDetection: fireDetection,
        licensePlateDetection: licensePlateDetection
      }
    }
  }, [selectedCamera, sessionPermissions])

  const isDisabled = React.useMemo(() => {
    return isEmpty(selectedEvents);
  }, [selectedEvents]);

  const startTimestamp = React.useMemo(() => {
    const f = first(selectedEvents);
    if (f) {
      return Utils.Timestamp.edgeTimestampToMiliseconds(f.startDate);
    }
    return undefined;
  }, [selectedEvents])

  const endTimestamp = React.useMemo(() => {
    const f = first(selectedEvents);
    if (f && (f.endDate || f?.currentEnd)) {
      return Utils.Timestamp.edgeTimestampToMiliseconds(f?.currentEnd || f.endDate);
    }

    return undefined;
  }, [selectedEvents])

  function havePermission(permissions: PermissionType[], lookFor: PermissionType[]): boolean {
    return permissions.some(el => [...lookFor, "ALL"].indexOf(el) >= 0);
  }

  return (
    <Box
      width="100%"
      height="100%"
      borderLeft="1px solid rgba(0, 0, 0, 0.12)"
      display="grid"
      gridTemplateRows="min-content auto"
      style={{
        opacity: isDisabled ? 0.5 : 1,
        pointerEvents: isDisabled ? 'none' : undefined
      }}
    >
      <Box paddingTop="1.5rem" paddingLeft="1.5rem" paddingRight="1.5rem" id="export-video-camera-selection" width="100%">
        <Title />
        <Typography variant="subtitle2">{`Camera`}</Typography>
        <Select
          IconComponent={ExpandMore}
          fullWidth
          variant="outlined"
          value={selectedCamera && cameraOptions.includes(selectedCamera) ? selectedCamera?.kvsName : ""}
          data-cy={`export-type-select`}
        >
          {cameraOptions.map((e) => (
            <MenuItem
              onClick={() => setSelectedCamera(e)}
              value={e.kvsName}
              data-cy={`export-type-video-menu-item`}
            >
              {e.defaultName || e.kvsName}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Box marginRight="1.5rem" marginLeft="1.5rem">
        <ExportsTab
          selectedSite={selectedSite}
          stream={selectedCamera}
          features={cameraFeatures}
          maxWidth="18rem"
          noTitle
          noMargin
          startTimestamp={startTimestamp}
          endTimestamp={endTimestamp}
          maxHeight="78vh"
        />
      </Box>
    </Box>
  )
}

export default EventInboxExportsPanel;
